import { useState, ReactNode } from 'react'
import { Link, NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext'
import { BellIcon } from './BellIcon'
import { LanguageSwitcher } from './LanguageSwitcher'

/**
 * Каркас authenticated-страниц: top-bar с логотипом и навигацией +
 * mobile-drawer + main content.
 */
/** Основные пункты — видны всегда. Логотип ведёт на /app, поэтому «Кабинет» не дублируется. */
const NAV_PRIMARY: Array<{ to: string; labelKey: string; icon: string }> = [
  { to: '/app/classes',     labelKey: 'appShell.navClasses',    icon: '📘' },
  { to: '/app/students',    labelKey: 'appShell.navStudents',   icon: '👥' },
  { to: '/app/journal',     labelKey: 'appShell.navJournal',    icon: '📊' },
  { to: '/app/attendance',  labelKey: 'appShell.navAttendance', icon: '✅' },
  { to: '/app/schedule',    labelKey: 'appShell.navSchedule',   icon: '📅' },
  { to: '/app/behavior',    labelKey: 'appShell.navBehavior',   icon: '⭐' },
  { to: '/app/chat',        labelKey: 'appShell.navChat',       icon: '💬' },
]

/** Вторичные — в выпадающем меню «Ещё». */
const NAV_SECONDARY: Array<{ to: string; labelKey: string; icon: string }> = [
  { to: '/app/homework',    labelKey: 'appShell.navHomework',   icon: '📝' },
  { to: '/app/documents',   labelKey: 'appShell.navDocuments',  icon: '📑' },
  { to: '/app/analytics',   labelKey: 'appShell.navAnalytics',  icon: '📈' },
  { to: '/app/subjects',    labelKey: 'appShell.navSubjects',   icon: '📚' },
  { to: '/app/templates',   labelKey: 'appShell.navTemplates',  icon: '🧩' },
  { to: '/app/school',      labelKey: 'appShell.navSchool',     icon: '🏫' },
  { to: '/app/billing',     labelKey: 'appShell.navBilling',    icon: '💳' },
  { to: '/app/audit',       labelKey: 'appShell.navAudit',      icon: '🔒' },
  { to: '/app/profile',     labelKey: 'appShell.navProfile',    icon: '⚙️' },
]

const NAV = [
  { to: '/app',             labelKey: 'appShell.navCabinet',    icon: '🏠' },
  ...NAV_PRIMARY,
  ...NAV_SECONDARY,
]

function RoleBadge({ role }: { role: string }) {
  const { t } = useTranslation()
  // TEACHER — без бейджа, иначе шум; завуч/директор/админ — подсвечиваем
  const labels: Record<string, { text: string; cls: string }> = {
    VICE_PRINCIPAL: { text: t('appShell.roleVicePrincipal'), cls: 'bg-blue-100 text-blue-800' },
    PRINCIPAL:      { text: t('appShell.rolePrincipal'),     cls: 'bg-purple-100 text-purple-800' },
    IT_ADMIN:       { text: t('appShell.roleItAdmin'),       cls: 'bg-gray-200 text-gray-700' },
    ADMIN:          { text: t('appShell.roleAdmin'),         cls: 'bg-red-100 text-red-800' },
    SUPER_ADMIN:    { text: t('appShell.roleSuperAdmin'),    cls: 'bg-amber-100 text-amber-900' },
  }
  const v = labels[role]
  if (!v) return null
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium whitespace-nowrap flex-shrink-0 ${v.cls}`}>
      {v.text}
    </span>
  )
}

export function AppShell({
  title,
  subtitle,
  actions,
  children,
}: {
  title?: string
  subtitle?: string
  actions?: ReactNode
  children: ReactNode
}) {
  const { teacher, logout } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const handleLogout = async () => { await logout(); navigate('/') }

  if (!teacher) return null

  const navSecondary = teacher.role === 'SUPER_ADMIN'
    ? [
        ...NAV_SECONDARY,
        { to: '/app/super-admin/billing', labelKey: 'appShell.navSchoolSubs', icon: '👑' },
      ]
    : NAV_SECONDARY

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-2 sm:gap-4">
          <Link to="/app" className="flex items-center gap-2 group flex-shrink-0">
            <img src="/edpad-logo.png" alt="EdPad" className="w-8 h-8 rounded transition-transform group-hover:scale-105" />
            <span className="font-semibold text-gray-900 hidden sm:inline">{t('appShell.brand')}</span>
          </Link>

          {/* Desktop nav — primary только, остальное в «Ещё» */}
          <nav className="hidden lg:flex items-center gap-0.5 ml-3 min-w-0">
            {NAV_PRIMARY.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) =>
                  `text-[13px] px-2.5 py-1.5 rounded-md transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-primary-soft text-primary font-medium'
                      : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                  }`
                }
              >
                {t(n.labelKey)}
              </NavLink>
            ))}

            {/* Кнопка «Ещё» с dropdown */}
            <div className="relative">
              <button
                onClick={() => setMoreOpen((v) => !v)}
                onBlur={() => setTimeout(() => setMoreOpen(false), 150)}
                className="text-[13px] px-2.5 py-1.5 rounded-md text-gray-600 hover:text-primary hover:bg-gray-50 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                {t('appShell.more')} ▾
              </button>
              {moreOpen && (
                <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[180px] z-40 animate-fade-in">
                  {navSecondary.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      onClick={() => setMoreOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                          isActive
                            ? 'bg-primary-soft text-primary font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`
                      }
                    >
                      <span className="text-base">{n.icon}</span>
                      {t(n.labelKey)}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Spacer — раздвигает nav и блок аккаунта */}
          <div className="flex-1 min-w-0" />

          <Link
            to="/"
            className="hidden md:inline text-sm text-gray-500 hover:text-primary flex-shrink-0 ml-4 pl-4 border-l border-gray-200"
            title={t('appShell.aboutProduct')}
          >
            {t('appShell.aboutProduct')}
          </Link>

          <LanguageSwitcher className="hidden md:block text-xs border border-gray-200 rounded-md px-1.5 py-1 bg-white text-gray-600 cursor-pointer flex-shrink-0" />

          <BellIcon />

          <div className="hidden md:block text-sm text-right max-w-[220px] flex-shrink-0">
            <div className="font-medium flex items-center justify-end gap-1.5 min-w-0">
              <span className="truncate">{teacher.displayName}</span>
              <RoleBadge role={teacher.role} />
            </div>
            <div className="text-xs text-gray-500 truncate">{teacher.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="hidden md:inline text-sm text-gray-600 hover:text-red-600 px-2 flex-shrink-0"
            title={t('appShell.logout')}
          >
            {t('appShell.logout')}
          </button>

          {/* Mobile burger */}
          <button
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={t('appShell.menu')}
            className="lg:hidden p-2 -mr-2 text-gray-700"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {menuOpen
                ? <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" />
                : (<><path d="M3 6h18" strokeLinecap="round" /><path d="M3 12h18" strokeLinecap="round" /><path d="M3 18h18" strokeLinecap="round" /></>)}
            </svg>
          </button>
        </div>

        {/* Mobile drawer */}
        {menuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white animate-fade-in">
            <nav className="max-w-7xl mx-auto px-4 py-2 flex flex-col">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === '/app'}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 py-2.5 px-3 rounded text-sm ${
                      isActive ? 'bg-primary-soft text-primary font-medium' : 'text-gray-700'
                    }`
                  }
                >
                  <span className="text-lg">{n.icon}</span>
                  {t(n.labelKey)}
                </NavLink>
              ))}
              <div className="border-t border-gray-100 mt-2 pt-2">
                <Link
                  to="/"
                  onClick={() => setMenuOpen(false)}
                  className="block text-sm text-gray-700 py-2 px-3 hover:bg-gray-50 rounded"
                >
                  🌐 {t('appShell.aboutProduct')}
                </Link>
                <div className="px-3 mt-2 mb-2">
                  <LanguageSwitcher className="w-full text-sm border border-gray-200 rounded-md px-2 py-2 bg-white text-gray-700 cursor-pointer" />
                </div>
                <div className="text-xs text-gray-500 px-3 mt-2 mb-1">{teacher.email}</div>
                <button
                  onClick={() => { setMenuOpen(false); handleLogout() }}
                  className="text-sm text-red-600 py-2 px-3 text-left w-full hover:bg-red-50 rounded"
                >
                  {t('appShell.logout')}
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Title block */}
      {(title || actions) && (
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-start sm:items-center justify-between gap-3 flex-col sm:flex-row">
            <div className="min-w-0">
              {title && <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">{title}</h1>}
              {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
            </div>
            {actions}
          </div>
        </div>
      )}

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 pb-40 md:pb-6">
        {children}
      </main>
    </div>
  )
}
